import { socket }                       from './socket';
import { subscribe, setUsername, setRoomCode, getState } from './store';
import { navigateTo }                   from './router';
import { toast, showStatusBar }         from './ui';
import { initAuth }                     from './auth-sheet';
import './screens/lobby';
import './screens/instruction';
import './screens/playing';
import './screens/results';
import './screens/session-end';

// ─── Boot ────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', async () => {
  await initAuth();   // handle OAuth redirect return before rendering
  initStatusBar();
  initEntryScreen();
  initJoinSheet();
  initUsernameSheet();
  navigateTo('entry');
});

// ─── Status bar (connection indicator) ───────────────────────────────────────

function initStatusBar(): void {
  const bar = document.getElementById('status-bar')!;
  socket.onStatus(status => {
    bar.className = '';
    if (status === 'reconnecting') {
      bar.classList.add('reconnecting');
      toast('Reconnecting…', 'error');
    }
    if (status === 'connected') {
      showStatusBar(bar);
    }
  });
}

// ─── Entry screen ────────────────────────────────────────────────────────────

function initEntryScreen(): void {
  document.getElementById('btn-create-room')!.addEventListener('click', () => {
    withUsername(() => createRoom());
  });

  document.getElementById('btn-join-room')!.addEventListener('click', () => {
    openSheet('sheet-join');
  });

  document.getElementById('btn-profile')!.addEventListener('click', () => {
    navigateTo('profile');
  });

  document.getElementById('btn-leaderboard')!.addEventListener('click', () => {
    navigateTo('leaderboard');
  });
}

// ─── Create room ─────────────────────────────────────────────────────────────

async function createRoom(): Promise<void> {
  const { username } = getState();
  if (!username) return;

  const btn = document.getElementById('btn-create-room') as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = 'Creating…';

  try {
    const roomCode = await socket.createRoom(username);
    setRoomCode(roomCode);
    navigateTo('lobby');
  } catch (err) {
    toast('Could not create room. Try again.', 'error');
    console.error(err);
  } finally {
    btn.disabled = false;
    btn.textContent = '🌋 Create Room';
  }
}

// ─── Join sheet ───────────────────────────────────────────────────────────────

function initJoinSheet(): void {
  const sheet   = document.getElementById('sheet-join')!;
  const backdrop = document.getElementById('sheet-join-backdrop')!;
  const boxes   = Array.from(document.querySelectorAll<HTMLInputElement>('.code-box'));
  const submitBtn = document.getElementById('btn-join-submit') as HTMLButtonElement;
  const errorEl  = document.getElementById('join-error')!;

  // Close on backdrop tap
  backdrop.addEventListener('click', () => closeSheet('sheet-join'));

  // Code box keyboard navigation
  boxes.forEach((box, i) => {
    box.addEventListener('input', () => {
      const val = box.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      box.value = val.slice(-1);
      box.classList.toggle('filled', !!box.value);
      if (val && i < boxes.length - 1) boxes[i + 1].focus();
      updateSubmitState();
    });

    box.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !box.value && i > 0) {
        boxes[i - 1].focus();
        boxes[i - 1].value = '';
        boxes[i - 1].classList.remove('filled');
        updateSubmitState();
      }
    });

    // Handle paste (e.g. paste "AB12" fills all boxes)
    box.addEventListener('paste', (e) => {
      e.preventDefault();
      const pasted = e.clipboardData?.getData('text')
        .toUpperCase().replace(/[^A-Z0-9]/g, '') ?? '';
      pasted.split('').slice(0, boxes.length).forEach((char, j) => {
        if (boxes[j]) { boxes[j].value = char; boxes[j].classList.add('filled'); }
      });
      boxes[Math.min(pasted.length, boxes.length - 1)].focus();
      updateSubmitState();
    });
  });

  function updateSubmitState(): void {
    const code = boxes.map(b => b.value).join('');
    submitBtn.disabled = code.length < boxes.length;
    errorEl.style.display = 'none';
    boxes.forEach(b => b.classList.remove('error'));
  }

  submitBtn.addEventListener('click', async () => {
    const code = 'LAVA-' + boxes.map(b => b.value).join('');
    const { username } = getState();

    submitBtn.disabled = true;
    submitBtn.textContent = 'Joining…';

    const doJoin = async () => {
      try {
        await socket.joinRoom(code, username!);
        setRoomCode(code);
        closeSheet('sheet-join');
        navigateTo('lobby');
      } catch {
        boxes.forEach(b => b.classList.add('error'));
        errorEl.textContent = 'Room not found. Check the code and try again.';
        errorEl.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.textContent = 'Join';
      }
    };

    if (!username) {
      withUsername(doJoin);
    } else {
      await doJoin();
    }
  });

  document.getElementById('btn-scan-qr')!.addEventListener('click', () => {
    toast('QR scanning coming soon', 'error');
  });
}

// ─── Username sheet ───────────────────────────────────────────────────────────

let usernameCallback: (() => void) | null = null;

function withUsername(callback: () => void): void {
  const { username } = getState();
  if (username) {
    callback();
    return;
  }
  usernameCallback = callback;
  openSheet('sheet-username');
  setTimeout(() => document.getElementById('input-username')?.focus(), 350);
}

function initUsernameSheet(): void {
  const backdrop  = document.getElementById('sheet-username-backdrop')!;
  const input     = document.getElementById('input-username') as HTMLInputElement;
  const confirmBtn = document.getElementById('btn-username-confirm') as HTMLButtonElement;

  backdrop.addEventListener('click', () => closeSheet('sheet-username'));

  input.addEventListener('input', () => {
    confirmBtn.disabled = input.value.trim().length < 2;
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !confirmBtn.disabled) confirmBtn.click();
  });

  confirmBtn.addEventListener('click', () => {
    const name = input.value.trim();
    if (name.length < 2) return;
    setUsername(name);
    closeSheet('sheet-username');
    if (usernameCallback) {
      usernameCallback();
      usernameCallback = null;
    }
  });
}

// ─── Sheet helpers ────────────────────────────────────────────────────────────

function openSheet(id: string): void {
  const sheet = document.getElementById(id);
  sheet?.classList.add('open');
  sheet?.removeAttribute('aria-hidden');
}

function closeSheet(id: string): void {
  const sheet = document.getElementById(id);
  sheet?.classList.remove('open');
  sheet?.setAttribute('aria-hidden', 'true');
}

// ─── Session state → screen routing ──────────────────────────────────────────

subscribe(({ session }) => {
  if (!session) return;
  switch (session.phase) {
    case 'LOBBY':           navigateTo('lobby');       break;
    case 'INSTRUCTION':     navigateTo('instruction'); break;
    case 'EVENT_GAME':      navigateTo('instruction'); break;
    case 'PLAYING':         navigateTo('playing');     break;
    case 'WAITING':         navigateTo('playing');     break;
    case 'RESULTS':
    case 'VOLCANO_ADVANCE': navigateTo('results');     break;
    case 'SESSION_END':     navigateTo('session-end'); break;
  }
});
